import React, { memo } from "react";
import { Post } from "@/core/types/postTypes";
import {
  PostCardContainer,
  PostMediaSection,
  PostBadge,
} from "@/components/PostCard";
import { getStatusConfig } from "@/core/utils/statusConfig";
import { usePostMedia } from "./usePostMedia";

interface SelectablePostCardProps {
  post: Post;
  selected: boolean;
  onSelect: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  standard: "Post",
  reel: "Reel",
  story: "Story",
};

/**
 * Visual post card for selection contexts (pickers, link modals).
 *
 * Composes the standard PostCard building blocks (PostCardContainer,
 * PostMediaSection, PostBadge) with usePostMedia for auth-aware
 * media rendering. No duplicated media or auth logic.
 */
export const SelectablePostCard: React.FC<SelectablePostCardProps> = memo(({
  post,
  selected,
  onSelect,
}) => {
  const {
    currentImage,
    imageCount,
    currentImageIndex,
    handleImageClick,
  } = usePostMedia(post);

  const centralConfig = getStatusConfig(post.status);
  const statusConfig = {
    bg: centralConfig.colors.background,
    text: centralConfig.colors.primary,
    border: centralConfig.colors.border,
    label: centralConfig.label,
  };
  const typeLabel = TYPE_LABELS[post.postType] || post.postType;
  const caption = post.caption?.slice(0, 80) || null;

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer transition-all duration-150 rounded-xl ${
        selected
          ? "ring-2 ring-blue-500 ring-offset-1"
          : "hover:ring-1 hover:ring-gray-300"
      }`}
    >
      <PostCardContainer viewMode="list">
        <div className="flex items-center gap-4">
          {/* Thumbnail via PostMediaSection */}
          <div className="relative shrink-0">
            {currentImage ? (
              <>
                <PostMediaSection
                  viewMode="list"
                  mediaItem={currentImage}
                  isReel={post.postType === "reel"}
                  isCarousel={post.isCarousel}
                  carouselIndex={currentImageIndex}
                  carouselTotal={imageCount}
                  onClick={post.isCarousel && imageCount > 1 ? handleImageClick : undefined}
                  className="w-16 h-16"
                />
                {post.isCarousel && imageCount > 1 && (
                  <div className="absolute -top-1 -right-1">
                    <PostBadge.CarouselCountBadge currentIndex={currentImageIndex} totalItems={imageCount} />
                  </div>
                )}
              </>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}
              >
                {statusConfig.label}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                {typeLabel}
              </span>
            </div>
            {caption ? (
              <p className="text-sm text-gray-700 truncate">{caption}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">No caption</p>
            )}
            <span className="text-xs text-gray-400 mt-0.5 block">
              {new Date(post.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      </PostCardContainer>
    </div>
  );
});

SelectablePostCard.displayName = "SelectablePostCard";
